import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, RefreshCw } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { useClient, useClientActions } from './state';
import { useFilingActions } from '@/features/filing/state';

export function ClientsPage() {
  const snapshot = useClient((value) => value);
  const actions = useClientActions();
  const filingActions = useFilingActions();
  useEffect(() => {
    if (snapshot.matches('idle')) actions.loadClients();
  }, [actions, snapshot]);

  const clients = snapshot.context.clients;
  return (
    <AppShell title="Clients" activePath="/clients">
      <div className="mx-auto max-w-5xl space-y-8 px-6 py-10">
        <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Client register</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">Clients</h1>
            <p className="mt-2 text-sm text-muted-foreground">The people and entities represented in this workspace.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => actions.loadClients()} disabled={snapshot.matches('loading')}>
              <RefreshCw className="mr-2 size-4" />
              Refresh
            </Button>
            <Button render={<Link to="/client-register" />}>Register client</Button>
          </div>
        </header>
        {snapshot.matches('loading') && (
          <p className="border border-border bg-muted/30 p-6 text-sm text-muted-foreground">Loading clients…</p>
        )}
        {snapshot.matches('error') && (
          <div className="border border-destructive/40 bg-destructive/5 p-6">
            <p className="text-sm">Clients could not be loaded.</p>
            <Button variant="outline" className="mt-4" onClick={() => actions.loadClients()}>
              Try again
            </Button>
          </div>
        )}
        {snapshot.matches('ready') && clients.length === 0 && (
          <div className="border border-dashed border-border p-10 text-center">
            <p className="font-heading text-lg">No clients registered yet</p>
            <p className="mt-2 text-sm text-muted-foreground">Register a client to begin a fiduciary file.</p>
            <Button render={<Link to="/client-register" />} className="mt-5">
              Register first client
            </Button>
          </div>
        )}
        {snapshot.matches('ready') && clients.length > 0 && (
          <div className="divide-y divide-border border-y border-border">
            {clients.map((client) => (
              <article key={client.clientId} className="flex flex-wrap items-center justify-between gap-4 py-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Client</p>
                  <h2 className="mt-1 font-heading text-lg font-medium">{client.clientName || 'Unnamed client'}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {client.fiduciaryName || 'Fiduciary not recorded'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  data-jr-key="open-filing"
                  onClick={() => filingActions.open(client.clientId, '', '')}
                >
                  <ArrowUpRight className="mr-2 size-4" />
                  Prepare return
                </Button>
              </article>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
